import { type ReactNode, type Ref, useEffect, useImperativeHandle, useLayoutEffect, useRef } from 'react';

import type { BoardStructure } from '../../app/structure.ts';
import { attractEngine, Clock, type Engine, HeroView } from './heroView.ts';

export interface HeroHandle {
	/** The board's box on screen (the hand-over's first frame), or null. */
	rect(): DOMRect | null;
	/** The answer the ghost is on, or null between chains. */
	ghost(): string | null;
	/** Stops the attract loop and lifts its sorts (Play was pressed). */
	freeze(): void;
}

interface HeroBoardProps {
	/** Structure only: the hero never receives a letter (see app/structure.ts). */
	structure: BoardStructure;
	/** `attract`: plays itself. `solved`: rests with `route` drawn in cobalt. */
	mode: 'attract' | 'solved';
	/** Solved: the best Flow chain's answer ids. */
	route?: readonly string[];
	/** Reduced motion: the still frame. */
	still: boolean;
	/** First arrival: the forme sets (clue blocks stamp in) before the ghost starts. */
	intro: boolean;
	label: string;
	/**
	 * Something perched on the board's top frame edge, at its right (Today: idle Nib).
	 * Kept outside the board itself, so it never sits inside the grid.
	 */
	perch?: ReactNode;
	ref?: Ref<HeroHandle>;
}

/** The perch's size (px): at most, and the least worth showing (below ~32px, Nib doesn't read). */
const PERCH_MAX = 56;
const PERCH_MIN = 36;
/** How far the perch may rise above the slot, into the title's bottom margin (px). */
const PERCH_SPILL = 10;

/**
 * Today's hero: the day's board, bare, playing itself (option B "Attract"). Sized to the
 * slot it sits in; a new structure (another level) lifts the old board away and settles
 * the new one.
 */
export function HeroBoard({ structure, mode, route, still, intro, label, perch, ref }: HeroBoardProps) {
	const outerRef = useRef<HTMLDivElement>(null);
	const slotRef = useRef<HTMLDivElement>(null);
	const viewRef = useRef<HeroView | null>(null);
	const engineRef = useRef<Engine | null>(null);
	const shownRef = useRef<BoardStructure | null>(null);

	useImperativeHandle(
		ref,
		() => ({
			rect: () => viewRef.current?.el.getBoundingClientRect() ?? null,
			ghost: () => engineRef.current?.current() ?? null,
			freeze: () => {
				engineRef.current?.stop();
				viewRef.current?.clear();
			},
		}),
		[],
	);

	// Mount (and re-mount on a new structure): build the board, fit it, start it.
	useLayoutEffect(() => {
		const slot = slotRef.current;
		if (!slot) return;
		const view = new HeroView(structure, label);
		const old = viewRef.current?.el ?? null;
		// Another level's board (not a re-run of this one's effect) swaps in with a lift.
		const swapping = shownRef.current !== null && shownRef.current !== structure && old !== null && old.isConnected;
		shownRef.current = structure;
		viewRef.current = view;
		const fit = () => {
			// Past the phone (J3) the page lets the hero's cells grow (today.css --hero-cell-max),
			// keeping --hero-reserve of the height free over the board for Nib's perch.
			const css = getComputedStyle(slot);
			const max = parseFloat(css.getPropertyValue('--hero-cell-max'));
			const reserve = parseFloat(css.getPropertyValue('--hero-reserve')) || 0;
			view.fitTo(slot.clientWidth, slot.clientHeight - 8 - reserve, Number.isFinite(max) && max > 0 ? max : undefined);
			// Where the perch stands: the board's top-right corner, in the slot's box. The
			// board keeps its size; the perch takes the headroom there is above it (plus the
			// title's bottom margin), shrinking to fit and stepping out when there's too little.
			const b = view.el;
			const outer = outerRef.current;
			if (b.isConnected && outer) {
				const nib = Math.min(PERCH_MAX, Math.floor(((b.offsetTop + PERCH_SPILL) * 120) / 106));
				outer.style.setProperty('--perch-x', `${b.offsetLeft + b.offsetWidth}px`);
				outer.style.setProperty('--perch-y', `${b.offsetTop}px`);
				outer.style.setProperty('--hero-nib', `${nib}px`);
				outer.dataset.perch = nib >= PERCH_MIN ? 'on' : 'off';
			}
		};
		const clock = new Clock();
		const start = () => {
			let engine: Engine;
			if (mode === 'solved') {
				view.route(route ?? [], still || !intro ? null : clock);
				engine = { stop: () => clock.stop(), current: () => null };
			} else {
				const a = attractEngine(view, { still, intro: intro || swapping });
				engine = {
					stop: () => {
						a.stop();
						clock.stop();
					},
					current: a.current,
				};
			}
			engineRef.current = engine;
		};
		if (swapping && !still && typeof old.animate === 'function') {
			old.style.position = 'absolute';
			old.animate([{ opacity: 1 }, { opacity: 0, transform: 'translateY(-8px) scale(.98)' }], {
				duration: 140,
				easing: 'ease-in',
				fill: 'forwards',
			});
			clock.later(() => {
				slot.replaceChildren(view.el);
				fit();
				view.el.animate?.([{ opacity: 0, transform: 'translateY(10px) scale(.97)' }, { opacity: 1, transform: 'none' }], {
					duration: 260,
					easing: 'cubic-bezier(.2,.9,.25,1)',
				});
				start();
			}, 140);
		} else {
			slot.replaceChildren(view.el);
			fit();
			start();
		}
		const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit);
		ro?.observe(slot);
		return () => {
			ro?.disconnect();
			clock.stop();
			engineRef.current?.stop();
			engineRef.current = null;
		};
		// `label`, `route` and `intro` are read once per structure/mode.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [structure, mode, still]);

	// Unmount: take the board with it.
	useEffect(
		() => () => {
			viewRef.current?.el.remove();
		},
		[],
	);

	// The board lives in `hero-stage` (replaced imperatively); the perch is React's, beside it.
	return (
		<div ref={outerRef} className="hero-slot">
			<div ref={slotRef} className="hero-stage" />
			{perch && <div className="hero-perch">{perch}</div>}
		</div>
	);
}
